<script>
  import { uniqueArray } from '$lib/utils/uniqueArray';
  import { twMerge } from 'tailwind-merge';
  // TODO: Replace MultiSelect with this component

  import { useClickOutside } from '$lib/utils/useClickOutside';
  import { setContext } from 'svelte';
  import { writable } from 'svelte/store';
  import Icon from '../Icon.svelte';
  import Input from './Input.svelte';

  let {
    name = undefined,
    value = $bindable([]),
    placeholder = 'Välj',
    disabled = false,
    label = null,
    onChange = () => {},
    valid = true,
    children = undefined,
  } = $props();

  let options = $state([]);
  let dropDownContent = $state();
  let expanded = $state(false);

  // We're binding the value to an input element, so we can get the data with FormData()
  // We don't use <select multiple /> in this case, since we only have access to the labels, not the values
  let inputElement = $state();

  const store = writable(value);
  const searchValue = writable('');

  /** Shallow value-equality for the flat selection arrays this component holds. */
  const sameSelection = (a, b) =>
    Array.isArray(a) &&
    Array.isArray(b) &&
    a.length === b.length &&
    a.every((v, i) => v === b[i]);

  $effect(() => {
    if (!sameSelection(value, $store)) value = $store;
  });
  $effect(() => {
    if (!sameSelection($store, value)) store.set(value);
  });

  const selectedLabels = $derived(
    options.filter((a) => $store.includes(a.value)).map((a) => a.label),
  );
  const numberOfSelectedIds = $derived(selectedLabels.length);

  // Plain $effect: measures the dropdown, so it needs the DOM in place.
  $effect(() => {
    if (!expanded || !dropDownContent) return;
    // Check if dropdown is outside window viewport
    const h = dropDownContent.getBoundingClientRect().height;
    const w = dropDownContent.getBoundingClientRect().width;
    const y = dropDownContent.getBoundingClientRect().top;
    const x = dropDownContent.getBoundingClientRect().left;
    const wh = window.innerHeight;
    const ww = window.innerWidth;
    const diffY = h + y - wh;
    const diffX = w + x - ww;

    // If it's outside, move it inside
    if (diffX > 0) {
      dropDownContent.style.setProperty('left', `-${diffX + 16}px`);
    }
    if (diffY > 0) {
      dropDownContent.style.setProperty('top', `-${diffY - 16}px`);
    }
  });

  const classes = $derived(
    twMerge([
      'pl-2 border pt-0.5',
      selectedLabels?.length > 0
        ? 'text-gray-3 placeholder:text-gray-3'
        : 'text-gray-6 placeholder:text-gray-6',
      disabled && 'placeholder:text-gray-3',
      selectedLabels?.length > 0 && 'pl-9 ',
      !valid &&
        'border-red hover:border-red dark:border-red dark:hover:border-red animate-shake',
    ]),
  );

  setContext('select', {
    store,
    searchValue,
    onChange: (value, checked) => {
      if (checked) store.set([...$store, value]);
      else store.set($store.filter((a) => a !== value));
      inputElement.value = $store;
      inputElement.dispatchEvent(new Event('change'));
      onChange($store);
    },
    // Sub components can register themselves
    register: (value, label) => {
      options = uniqueArray([...options, { value, label }]);
    },
    // unregister: (value) => {
    // 	options = options.filter((a) => a.value !== value)
    // }
  });

  const toggleExpanded = () => !disabled && (expanded = !expanded);

  const expand = () => !disabled && (expanded = true);

  const collapse = () => {
    expanded = false;
    $searchValue = '';
  };
</script>

<!-- Input tag for use with FormData -->
<input bind:this={inputElement} class="hidden" {name} value={selectedLabels} />

<div>
  {#if label}
    <div class="mb-1 font-mono text-white">{label}</div>
  {/if}

  <div class="relative w-full" use:useClickOutside onclickOutside={collapse}>
    <Input
      placeholder={selectedLabels?.length > 0
        ? selectedLabels.join(', ')
        : placeholder}
      class={classes}
      {disabled}
      bind:value={$searchValue}
      onfocus={expand}
    />
    {#if numberOfSelectedIds > 0}
      <div class="absolute left-2 top-2 flex items-center justify-center">
        <div
          class="inline-block rounded-full bg-gray-5 px-2 text-sm text-white"
        >
          {numberOfSelectedIds}
        </div>
      </div>
    {/if}

    <!-- {#if expanded} -->
    <div
      bind:this={dropDownContent}
      class="absolute z-10 mt-1 max-h-56 min-w-full translate-y-0 space-y-2 overflow-y-auto whitespace-nowrap rounded-sm border bg-gray-9 p-2 opacity-100 transition
			{!expanded && 'invisible translate-y-1 opacity-0'}"
    >
      {@render children?.()}
    </div>
    <!-- {/if} -->

    <button
      onclick={toggleExpanded}
      type="button"
      class="bg-black-9 flex items-center {disabled &&
        'dark:bg-gray-9'} absolute bottom-px right-px top-px rounded-full px-1 transition"
    >
      {#if !disabled}
        <Icon size="lg" icon="expand_more" />
      {/if}
    </button>
  </div>
</div>
